import * as Sentry from '@sentry/react';
import { useEffect, useState, type FC, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

import { ANALYTIC_EVENTS, Analytics } from 'analytics/Analytics';
import { FORM_KEY } from 'constants/form-ids';
import { ErrorStatuses, PAY_TYPE, PaymentStatus } from 'constants/payment';
import { SEARCH_PARAMS } from 'constants/search-params';
import { useYM } from 'hooks';
import { startOverlayLoader, stopOverlayLoader } from 'store/app/slice';
import { useAppDispatch } from 'store/hooks';
import { useGetDataQuery } from 'store/payment/service';
import {
  setFormKey,
  setIsInvoice,
  setIsPayment,
  setIsValidForCheck,
  setPaymentData,
} from 'store/payment/slice';
import { createAndSubmit3DSSecureForm, getQueryParams } from 'utils';
import { CheckView } from 'views/components/base/check-view';
import { MainLayout } from 'views/layouts';
import { LoadingState } from 'views/shared/loading-state';

interface LoadDataContainerPropTypes {
  children: ReactNode;
  withoutLayout?: boolean;
  formKey: string;
}

const maxCountRequests = 5;

const LoadDataContainer: FC<LoadDataContainerPropTypes> = ({
  children,
  formKey,
  withoutLayout,
}) => {
  const location = useLocation();
  const [countRequests, setCountRequests] = useState(0);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const { trackPageView } = useYM();
  const { data, isLoading, refetch } = useGetDataQuery({
    url: location.pathname,
    params: location.search,
  });
  const dispatch = useAppDispatch();

  const formDefaultOrKase = formKey === FORM_KEY.DEFAULT || formKey === FORM_KEY.KASE;

  useEffect(() => {
    if (formKey) {
      dispatch(setFormKey(formKey));
    }
  }, [formKey, dispatch]);

  useEffect(() => {
    trackPageView();
    dispatch(setIsInvoice(location.pathname.includes('invoice')));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, dispatch]);

  useEffect(() => {
    const intervalHoldUpdate = setInterval(() => {
      if (data?.status === PaymentStatus.WAIT_PROCESSING && !data.redirectUser && !isRedirecting) {
        refetch();
      }

      if (data?.status === PaymentStatus.HOLDED && formDefaultOrKase && !isRedirecting) {
        refetch();
      }
    }, 3 * 1000);

    return () => clearInterval(intervalHoldUpdate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    const intervalHoldUpdate = setInterval(() => {
      if (countRequests < maxCountRequests && data?.status === PaymentStatus.PAYMENT_NOTFOUND) {
        refetch().then(() => {
          setCountRequests(countRequests + 1);
        });
      } else {
        clearInterval(intervalHoldUpdate);
      }
    }, 3 * 1000);
    dispatch(stopOverlayLoader());

    return () => clearInterval(intervalHoldUpdate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, countRequests, refetch]);

  useEffect(() => {
    if (!data) {
      return;
    }

    if (
      data.status === PaymentStatus.PAYMENT_NOTFOUND ||
      data.status === PaymentStatus.WAIT_PROCESSING ||
      data.status === PaymentStatus.WAIT_PAYMENT_CONFIRM
    ) {
      dispatch(startOverlayLoader());
    }

    if (data.acsData?.acsUrl) {
      createAndSubmit3DSSecureForm(data.acsData);
    }

    if (data.redirectUser && data.redirectUrl) {
      Analytics.sentEvent(ANALYTIC_EVENTS.redirect);
      dispatch(startOverlayLoader());
      setIsRedirecting(true);
      window.location.href = data.redirectUrl;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (!data) {
      return;
    }

    if (isRedirecting) {
      return;
    }

    const filteredAcquirings = data?.acquirings?.filter(
      (item, index, self) => index === self.findIndex((obj) => obj.formType === item.formType),
    );

    if (data?.status === PaymentStatus.HOLDED || data?.status === PaymentStatus.PAYMENT_SUCCESS) {
      dispatch(setIsValidForCheck(true));
    } else {
      dispatch(setIsValidForCheck(false));
    }

    if (countRequests < maxCountRequests && data?.status === PaymentStatus.PAYMENT_NOTFOUND) {
      return;
    }

    if (ErrorStatuses.includes(data.status)) {
      Sentry.captureMessage(`${data.error}
        ${JSON.stringify(data)}
      `);
    }

    if (
      data.status !== PaymentStatus.INTERNAL_ERROR &&
      data.status !== PaymentStatus.INCORRECT_DATA
    ) {
      Analytics.sentEvent(ANALYTIC_EVENTS.viewSuccess);
    } else {
      Analytics.sentEvent(ANALYTIC_EVENTS.viewError, {
        transaction_id: getQueryParams(SEARCH_PARAMS.ID),
        message: JSON.stringify(data),
      });
    }

    if (
      data?.status !== PaymentStatus.WAIT_PROCESSING &&
      data?.status !== PaymentStatus.WAIT_PAYMENT_CONFIRM
    ) {
      dispatch(stopOverlayLoader());
      dispatch(setPaymentData({ ...data, acquirings: filteredAcquirings }));
      dispatch(setIsPayment(data.paymentType === PAY_TYPE.PAYMENT));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, countRequests, isRedirecting]);

  if (isLoading) {
    return (
      <MainLayout formKey={formKey}>
        <div className="w-full flex-grow items-center flex justify-center">
          <LoadingState formKey={formKey} />
        </div>
      </MainLayout>
    );
  }

  const renderForm = () => {
    if (!data) {
      return <div />;
    }

    if (withoutLayout) {
      return <div>{children}</div>;
    }

    if (data?.status === PaymentStatus.HOLDED && formDefaultOrKase) {
      return (
        <div>
          <CheckView />
        </div>
      );
    }

    return <MainLayout formKey={formKey}>{children}</MainLayout>;
  };

  return renderForm();
};

export { LoadDataContainer };
